import ImageQualityStatus from '../../enums/ImageQualityStatus.js';
import VoxelReductions, { registerVoxelReductionsConstant, } from '../../enums/VoxelReductions.js';
const reductionDefinitions = new Map();
const registeredConstantNames = [];
let hasRegisteredCoreVoxelReductions = false;
function registerCoreVoxelReductions() {
    if (hasRegisteredCoreVoxelReductions) {
        return;
    }
    hasRegisteredCoreVoxelReductions = true;
    registerVoxelReduction({
        reduction: VoxelReductions.None,
        aliases: false,
        description: 'The data holds every voxel of its own grid.',
    });
    registerVoxelReduction({
        reduction: VoxelReductions.BoxAverage,
        aliases: false,
        description: 'The mean of the source voxels of one box.',
    });
    registerVoxelReduction({
        reduction: VoxelReductions.BoxForegroundMajority,
        aliases: false,
        description: 'The majority non-zero label of the source voxels of one box. It reduces labelmaps.',
    });
    registerVoxelReduction({
        reduction: VoxelReductions.Decimation,
        aliases: true,
        description: 'Every n-th source voxel. It folds the high spatial frequencies into the signal.',
    });
}
function registerVoxelReduction({ reduction, name, aliases, description, }) {
    if (!reduction) {
        throw new Error('registerVoxelReduction: the definition needs a reduction');
    }
    if (reductionDefinitions.has(reduction)) {
        throw new Error(`Voxel reduction "${reduction}" is already registered`);
    }
    if (typeof aliases !== 'boolean') {
        throw new Error(`Voxel reduction "${reduction}" must state whether it aliases`);
    }
    if (name && Object.prototype.hasOwnProperty.call(VoxelReductions, name)) {
        throw new Error(`Voxel reduction constant "${String(name)}" already exists`);
    }
    reductionDefinitions.set(reduction, { reduction, aliases, description });
    if (name) {
        registerVoxelReductionsConstant(name, reduction);
        registeredConstantNames.push(name);
    }
}
function getVoxelReduction(reduction) {
    registerCoreVoxelReductions();
    return reductionDefinitions.get(reduction);
}
function isAliasingReduction(reduction) {
    return getVoxelReduction(reduction)?.aliases !== false;
}
function __resetVoxelReductionRegistry() {
    reductionDefinitions.clear();
    hasRegisteredCoreVoxelReductions = false;
    for (const name of registeredConstantNames) {
        delete VoxelReductions[name];
    }
    registeredConstantNames.length = 0;
}
function imageQualityStatusOfRecord(record) {
    if (record.missing >= record.voxels) {
        return ImageQualityStatus.FAR_REPLICATE;
    }
    if (record.missing > 0) {
        return Math.min(record.lowest ?? ImageQualityStatus.FAR_REPLICATE, ImageQualityStatus.ADJACENT_REPLICATE);
    }
    const lowest = record.lowest ?? ImageQualityStatus.FAR_REPLICATE;
    if (isAliasingReduction(record.reduction)) {
        return Math.min(lowest, ImageQualityStatus.SUBRESOLUTION);
    }
    return lowest;
}
function compareVoxelQuality(record, requirement = {}) {
    const { displaySpacing, missingAllowed = 0, minStatus } = requirement;
    const causes = [];
    let magnitude = 0;
    if (displaySpacing) {
        for (let axis = 0; axis < 3; axis++) {
            const display = displaySpacing[axis];
            if (!(display > 0)) {
                continue;
            }
            magnitude = Math.max(magnitude, record.grid.spacing[axis] / display);
        }
        if (magnitude > 1 + 1e-6) {
            causes.push('resolution');
        }
    }
    if (isAliasingReduction(record.reduction)) {
        causes.push('aliasing');
    }
    if (record.missing > missingAllowed) {
        causes.push('missingData');
    }
    if (minStatus !== undefined && record.status < minStatus) {
        causes.push('quality');
    }
    return { lossless: causes.length === 0, causes, magnitude, record };
}
export { registerVoxelReduction, getVoxelReduction, isAliasingReduction, imageQualityStatusOfRecord, compareVoxelQuality, __resetVoxelReductionRegistry, };
